import OpenRoutes from "./OpenRoutes";
import AuthRoutes from "./AuthRoutes";
import { OrderSSEProvider } from "../contexts/OrderSSEContext";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState, useCallback } from "react";
import { firebaseLogin } from "../store/login.slice";
import { getSession, deleteSession, setSession } from "../utils/session";
import {
  isShopAdmin,
  isShopPicker,
  isShopSupervisor,
  isShopUser,
  isShopStockManager,
} from "../utils/roles";
import { fetchProductSpecDetails } from "../utils/productSpec";
import { fetchTaxonomyDetails } from "../utils/taxonomy";
import { getAuth, onAuthStateChanged, signOut } from "@firebase/auth";
import { subscribeUserToTopic } from "../services/topicSubscription";
import { getFirebaseMessagingToken } from "../services/firebaseMessaging";
import { setAllShops, updateShopDetails } from "../store/allShops.slice";
import store from "../store";
import shopService from "../services/allShops";
import { updateShopId } from "../store/allOrders.slice";
import * as Sentry from "@sentry/react";
import Clarity from "@microsoft/clarity";
import { clearPersistedState } from "../store/sagaStore";
import FullScreenSpinner from "../components/FullScreenSpinner/FullScreenSpinner";
import LoginService from "../services/login";
import { resetAuthState, getRoleFromFirebaseToken, hasValidAuthSession } from "../utils/firebaseAuth";

// ============================================================================
// CONSTANTS
// ============================================================================
const AUTH_TIMEOUT_MS = 15000; // 15 seconds - max time for entire auth flow
const INIT_TIMEOUT_MS = 5000; // 5 seconds - max time for Firebase to initialize
const SHOW_REFRESH_AFTER_MS = 10000; // 10 seconds - show refresh button
const SPINNER_LOGOUT_TIMEOUT_MS = 30000; // 30 seconds - force logout if spinner shows too long
const ROLE_FETCH_TIMEOUT_MS = 10000; // 10 seconds - max time to fetch role from Firebase
const SHOP_FETCH_TIMEOUT_MS = 10000; // 10 seconds - timeout per shop/config fetch attempt
const SHOP_FETCH_MAX_RETRIES = 5; // Max retry attempts for shop/config fetch

// ============================================================================
// TYPES
// ============================================================================
type AuthState = "initializing" | "loading" | "authenticated" | "unauthenticated";

// ============================================================================
// HELPER: Force hard redirect - clears all React state
// ============================================================================
const forceRedirectToLogin = async () => {
  // Sign out from Firebase first - this is critical to prevent loops
  // where onAuthStateChanged sees a stale user after redirect
  try {
    const auth = getAuth();
    if (auth.currentUser) {
      await signOut(auth);
    }
  } catch (e) {
    Sentry.captureException(e, {
      level: "error",
      tags: { auth_flow: "force_redirect_signout" },
      extra: { error: e instanceof Error ? e.message : "Unknown error" },
    });
  }

  // Synchronously clear everything before redirect
  try {
    // Reset Firebase auth utility state
    resetAuthState();
    
    // Clear session data
    deleteSession("customToken");
    deleteSession("userId");
    deleteSession("role");
    deleteSession("idToken");
    deleteSession("lastRequestTime");
    
    // Clear persisted Redux state
    clearPersistedState();
    
    // Reset LoginService mutex
    LoginService.resetFirebaseLoginMutex();
    
    // Clear Sentry user context
    Sentry.setUser(null);
  } catch (e) {
    // Ignore errors during cleanup
  }
  
  // Hard redirect - this clears all React state and re-initializes the app
  window.location.href = "/login";
};

// ============================================================================
// HELPER: Fetch with timeout and retry
// ============================================================================
const fetchWithRetry = async <T,>(
  fn: () => Promise<T>,
  label: string,
  maxRetries: number = SHOP_FETCH_MAX_RETRIES,
  timeoutMs: number = SHOP_FETCH_TIMEOUT_MS,
): Promise<T> => {
  let lastError: unknown;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const result = await Promise.race([
        fn(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error(`${label} timed out after ${timeoutMs}ms`)), timeoutMs),
        ),
      ]);
      return result;
    } catch (error) {
      lastError = error;
      Sentry.addBreadcrumb({
        category: "shop_prefetch",
        message: `${label} attempt ${attempt}/${maxRetries} failed`,
        level: "warning",
        data: {
          attempt,
          error: error instanceof Error ? error.message : String(error),
        },
      });

      // Don't wait after the last attempt
      if (attempt < maxRetries) {
        await new Promise((resolve) => setTimeout(resolve, Math.pow(2, attempt - 1) * 1000));
      }
    }
  }

  throw lastError;
};

// ============================================================================
// HELPER: Prefetch shops list synchronously before navigation
// Sets shopId to the first valid shop so <ShopDataGate> can begin fetching
// config + details as soon as the auth navigation completes. Shop config /
// details themselves are fetched by useShopConfig via the gate, not here.
// ============================================================================
const prefetchShopsForLogin = async (
  role: string,
  dispatchFn: typeof import("../store").default.dispatch,
): Promise<{ firstShop: any | null }> => {
  const userId = getSession("userId");

  try {
    const shopsResponse = await fetchWithRetry(
      () => shopService.fetchAllShopsForLogin(),
      "Shop list fetch",
    );
    const shops = shopsResponse?.data?.result?.data?.shops || [];
    dispatchFn(setAllShops(shops));

    const firstShop = shops.find(
      (shop: any) => shop?.firestore?.id && shop?.firestore?.shop_name,
    );
    if (firstShop?.firestore?.id) {
      dispatchFn(updateShopId(firstShop.firestore.id));
    } else {
      Sentry.addBreadcrumb({
        category: "shop_prefetch",
        message: "No valid shop found in shops list",
        level: "info",
        data: { userId, role, shopCount: shops.length },
      });
    }
    return { firstShop: firstShop ?? null };
  } catch (error) {
    Sentry.captureException(error, {
      level: "error",
      tags: {
        feature: "shop_prefetch",
        step: "fetch_shops",
        role,
      },
      extra: {
        userId,
        role,
        maxRetries: SHOP_FETCH_MAX_RETRIES,
        timeoutMs: SHOP_FETCH_TIMEOUT_MS,
        errorMessage: error instanceof Error ? error.message : String(error),
      },
    });
    return { firstShop: null };
  }
};

// ============================================================================
// COMPONENT
// ============================================================================
const AppRouter = () => {
  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  const [authState, setAuthState] = useState<AuthState>("initializing");
  const [userRole, setUserRole] = useState<string | null>(null);
  const [showRefresh, setShowRefresh] = useState(false);

  // ---------------------------------------------------------------------------
  // Redux
  // ---------------------------------------------------------------------------
  const dispatch = useDispatch();
  const firebaseLoginState = useSelector((state: any) => state.login.firebaseLogin);
  const beLoginState = useSelector((state: any) => state.login.beLogin);
  const googleLoginState = useSelector((state: any) => state.login.googleLogin);


  // ---------------------------------------------------------------------------
  // Router
  // ---------------------------------------------------------------------------
  const navigate = useNavigate();
  const location = useLocation();

  // ---------------------------------------------------------------------------
  // Refs - for preventing duplicate operations and tracking state
  // ---------------------------------------------------------------------------
  const authListenerSetupRef = useRef(false);
  const firebaseLoginDispatchedRef = useRef(false);
  const shopsFetchedRef = useRef(false);
  const topicSubscribedRef = useRef(false);
  const initTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const authTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track if we're in the middle of a logout to prevent race conditions
  const isLoggingOutRef = useRef(false);
  // Refs to track current login state for use in onAuthStateChanged callback
  const beLoginLoadingRef = useRef(false);
  const googleLoginLoadingRef = useRef(false);
  const firebaseLoginLoadingRef = useRef(false);
  const locationPathnameRef = useRef(location.pathname);
  const authStateRef = useRef<AuthState>(authState);

  // Keep refs in sync with state
  useEffect(() => {
    beLoginLoadingRef.current = beLoginState.loading;
  }, [beLoginState.loading]);
  
  useEffect(() => {
    googleLoginLoadingRef.current = googleLoginState.loading;
  }, [googleLoginState.loading]);
  
  useEffect(() => {
    firebaseLoginLoadingRef.current = firebaseLoginState.loading;
  }, [firebaseLoginState.loading]);
  
  useEffect(() => {
    locationPathnameRef.current = location.pathname;
  }, [location.pathname]);
  
  useEffect(() => {
    authStateRef.current = authState;
  }, [authState]);

  // ---------------------------------------------------------------------------
  // Environment
  // ---------------------------------------------------------------------------
  const isLocalMode = import.meta.env.MODE === "localdev";

  // ---------------------------------------------------------------------------
  // Helper: Clear all timeouts
  // ---------------------------------------------------------------------------
  const clearAllTimeouts = useCallback(() => {
    if (initTimeoutRef.current) {
      clearTimeout(initTimeoutRef.current);
      initTimeoutRef.current = null;
    }
    if (authTimeoutRef.current) {
      clearTimeout(authTimeoutRef.current);
      authTimeoutRef.current = null;
    }
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Helper: Get role with fallback to Firebase token claims
  // ---------------------------------------------------------------------------
  const getRoleWithFallback = useCallback(async (): Promise<string> => {
    // Try localStorage first (fast)
    let role = getSession("role");
    
    if (role && role.trim() !== "") {
      return role;
    }
    
    // Fallback: Get role from Firebase token claims
    Sentry.addBreadcrumb({
      category: "auth",
      message: "Role not in session, fetching from Firebase token claims",
      level: "info",
    });
    
    try {
      // Set a timeout for fetching role from Firebase
      const rolePromise = getRoleFromFirebaseToken();
      const timeoutPromise = new Promise<string>((resolve) => {
        setTimeout(() => resolve(""), ROLE_FETCH_TIMEOUT_MS);
      });
      
      role = await Promise.race([rolePromise, timeoutPromise]);
      
      if (role && role.trim() !== "") {
        // Cache the role in session for future use
        setSession("role", role);
        Sentry.addBreadcrumb({
          category: "auth",
          message: "Role fetched from Firebase and cached",
          level: "info",
          data: { role },
        });
        return role;
      }
    } catch (error) {
      Sentry.captureException(error, {
        level: "warning",
        tags: { auth_flow: "role_fetch_fallback" },
      });
    }
    
    return "";
  }, []);

  // ---------------------------------------------------------------------------
  // Helper: Check if login is in progress
  // ---------------------------------------------------------------------------
  const isLoginInProgress = useCallback(() => {
    return beLoginLoadingRef.current || 
           googleLoginLoadingRef.current || 
           firebaseLoginLoadingRef.current || 
           LoginService.isFirebaseLoginInProgress();
  }, []);

  // ---------------------------------------------------------------------------
  // Effect: Initialize auth state from Firebase (runs once)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    // Prevent multiple listener setups
    if (authListenerSetupRef.current) {
      return;
    }
    authListenerSetupRef.current = true;
    
    const auth = getAuth();
    Sentry.addBreadcrumb({
      category: "auth",
      message: "Setting up onAuthStateChanged listener",
      level: "info",
      data: { currentUser: auth.currentUser?.uid },
    });
    
    // Start initialization timeout - if Firebase doesn't respond in time, handle it
    initTimeoutRef.current = setTimeout(() => {
      if (authStateRef.current === "initializing") {
        Sentry.captureMessage("Firebase auth initialization timeout", {
          level: "warning",
          tags: { auth_flow: "init_timeout" },
        });
        
        // Check if we have session data to determine next state
        const hasAuthSession = hasValidAuthSession();
        const role = getSession("role");
        
        if (hasAuthSession && role) {
          // We have session data - try to proceed as authenticated
          setUserRole(role);
          setAuthState("authenticated");
        } else if (hasAuthSession && !role) {
          // Have auth session but no role - try firebaseLogin
          setAuthState("loading");
          if (!firebaseLoginDispatchedRef.current && !LoginService.isFirebaseLoginInProgress()) {
            firebaseLoginDispatchedRef.current = true;
            dispatch(firebaseLogin());
          }
        } else {
          // No session data - go to login
          setAuthState("unauthenticated");
        }
      }
    }, INIT_TIMEOUT_MS);
    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // Clear init timeout since Firebase responded
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
        initTimeoutRef.current = null;
      }
      
      // If we're logging out, ignore auth state changes
      if (isLoggingOutRef.current) {
        return;
      }
      
      const loginInProgress = isLoginInProgress();
      
      Sentry.addBreadcrumb({
        category: "auth",
        message: "onAuthStateChanged fired",
        level: "info",
        data: {
          hasUser: !!user,
          userId: user?.uid,
          pathname: locationPathnameRef.current,
          hasAuthSession: hasValidAuthSession(),
          hasRole: !!getSession("role"),
          loginInProgress,
        },
      });
      
      if (user) {
        // User exists in Firebase - try to get role (with fallback to token claims)
        const role = await getRoleWithFallback();
        
        if (role && role.trim() !== "") {
          // Role found - user is authenticated
          setUserRole(role);
          setAuthState("authenticated");
          
          // Set Sentry/Clarity user context
          if (!isLocalMode) {
            Sentry.setUser({ id: user.uid, email: user.email || undefined });
            Clarity.identify(user.uid);
          }
        } else {
          // No role found - check if login is in progress
          const hasAuthSession = hasValidAuthSession();
          const isOnLoginPage = locationPathnameRef.current === "/login";
          
          if (loginInProgress) {
            // Login in progress - stay in loading state and wait
            setAuthState("loading");
            // Don't do anything else - let the firebaseLogin effect handle completion
          } else if (hasAuthSession && !firebaseLoginDispatchedRef.current) {
            // Have auth session but no role and no login in progress - try firebaseLogin
            firebaseLoginDispatchedRef.current = true;
            setAuthState("loading");
            dispatch(firebaseLogin());
          } else if (!hasAuthSession && !isOnLoginPage) {
            // No auth session and not on login page - stale session
            Sentry.captureMessage("Firebase user exists but no role or customToken - signing out", {
              level: "warning",
              tags: { auth_flow: "stale_session" },
              extra: { userId: user.uid },
            });
            forceRedirectToLogin();
          } else if (!hasAuthSession && isOnLoginPage) {
            // On login page with no auth session - user needs to login
            setAuthState("unauthenticated");
          }
        }
      } else {
        // No Firebase user
        const hasAuthSession = hasValidAuthSession();
        
        if (hasAuthSession) {
          // We have an auth session but no Firebase user - unusual state
          if (locationPathnameRef.current === "/login") {
            // On login page - Login.tsx handles firebaseLogin dispatch
            // Stay in current state and wait
            if (loginInProgress) {
              setAuthState("loading");
            }
            // Don't set unauthenticated - let Login.tsx handle the flow
          } else if (!firebaseLoginDispatchedRef.current && !LoginService.isFirebaseLoginInProgress()) {
            // Not on login page - we need to dispatch firebaseLogin
            firebaseLoginDispatchedRef.current = true;
            setAuthState("loading");
            dispatch(firebaseLogin());
          } else {
            setAuthState("loading");
          }
        } else {
          // No custom token - user is definitely not authenticated
          setAuthState("unauthenticated");
        }
      }
    });

    return () => {
      unsubscribe();
      // Reset the ref so if the effect re-runs (StrictMode), it can set up again
      authListenerSetupRef.current = false;
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
        initTimeoutRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - this effect should only run once on mount

  // ---------------------------------------------------------------------------
  // Effect: Handle firebaseLogin saga completion - THIS IS THE KEY EFFECT
  // ---------------------------------------------------------------------------
  useEffect(() => {
    // If logging out, ignore
    if (isLoggingOutRef.current) {
      return;
    }
    
    // Track when firebaseLogin starts - set loading state
    if (firebaseLoginState.loading) {
      firebaseLoginDispatchedRef.current = true;
      if (authState !== "loading") {
        setAuthState("loading");
      }
      return; // Wait for completion
    }
    
    // Handle completion - ONLY when not loading and we have dispatched
    if (!firebaseLoginState.loading && firebaseLoginDispatchedRef.current) {
      if (firebaseLoginState.data?.uid) {
        // Firebase login succeeded - NOW get role from session
        // LoginService.firebaseToken() should have set the role
        const role = getSession("role");
        
        Sentry.addBreadcrumb({
          category: "auth",
          message: "firebaseLogin succeeded",
          level: "info",
          data: { uid: firebaseLoginState.data.uid, hasRole: !!role },
        });
        
        if (role && role.trim() !== "") {
          setUserRole(role);
          setAuthState("authenticated");
        } else {
          // Role still not set - try to get from Firebase token claims as last resort
          getRoleWithFallback().then((fetchedRole) => {
            if (fetchedRole && fetchedRole.trim() !== "") {
              setUserRole(fetchedRole);
              setAuthState("authenticated");
            } else {
              // Still no role - this is a critical error
              Sentry.captureMessage("No role found after successful firebaseLogin", {
                level: "error",
                tags: { auth_flow: "no_role_after_login" },
              });
              forceRedirectToLogin();
            }
          });
        }
      } else if (firebaseLoginState.error) {
        // Firebase login failed
        Sentry.captureException(firebaseLoginState.error, {
          level: "error",
          tags: { auth_flow: "firebase_login_failure" },
          extra: { 
            errorMessage: firebaseLoginState.error?.message,
            errorCode: firebaseLoginState.error?.code,
          },
        });
        
        // Clear custom token to prevent loops
        LoginService.clearCustomToken();
        
        // If on login page, just set unauthenticated - Login.tsx will show error
        if (location.pathname === "/login") {
          setAuthState("unauthenticated");
          // Reset dispatch ref so user can try again
          firebaseLoginDispatchedRef.current = false;
        } else {
          // Hard redirect to login
          forceRedirectToLogin();
        }
      }
    }
  }, [
    firebaseLoginState.loading,
    firebaseLoginState.data?.uid,
    firebaseLoginState.error,
    authState,
    getRoleWithFallback,
    location.pathname,
  ]);

  // ---------------------------------------------------------------------------
  // Effect: Handle navigation after authentication
  // ---------------------------------------------------------------------------
  useEffect(() => {
    // If logging out, don't navigate
    if (isLoggingOutRef.current) {
      return;
    }
    
    const handleNavigation = async () => {
      if (authState === "authenticated" && userRole) {
        // User is authenticated with a valid role
        if (location.pathname === "/login" || location.pathname === "/") {
          // Calculate target route based on role
          let targetRoute = isShopUser(userRole) ? "/orders" : "/dashboard";
          const from = location.state?.from?.pathname && location.state?.from?.pathname !== "/"
            ? location.state?.from?.pathname
            : null;

          try {
            if (isShopUser(userRole)) {
              // Synchronously fetch shops list (and set shopId) BEFORE
              // navigating. Shop config + details are fetched by useShopConfig
              // via the <ShopDataGate> — they load behind a full-screen loader,
              // not synchronously blocking navigation.
              const { firstShop } = await prefetchShopsForLogin(userRole, dispatch);
              shopsFetchedRef.current = true;

              // SHOP_STOCK_MANAGER lands on catalog page of first shop
              if (isShopStockManager(userRole) && !from && firstShop?.firestore?.id) {
                targetRoute = `/single-shop-catalog/${firstShop.firestore.id}`;
                // Populate shop detail so the catalog page title renders on
                // direct landing (mirrors SiderComponent navigation behavior)
                dispatch(updateShopDetails(firstShop));
              }
            } else if (location.state?.from?.pathname && !isShopPicker(userRole)) {
              // Fetch taxonomy/productSpec for non-picker users if navigating to a saved route
              await Promise.all([
                fetchTaxonomyDetails(dispatch),
                fetchProductSpecDetails(dispatch),
              ]);
            }
          } catch (error) {
            Sentry.captureException(error, {
              level: "warning",
              tags: { auth_flow: "prefetch_data" },
            });
            // Continue with navigation even if prefetch fails
          }

          navigate(from || targetRoute, { replace: true });
        }
      } else if (authState === "unauthenticated") {
        // User is not authenticated
        if (location.pathname !== "/login") {
          navigate("/login", { state: { from: location }, replace: true });
        }
      }
      // If authState is "loading" or "initializing", don't navigate - wait
    };

    handleNavigation();
  }, [authState, userRole, location.pathname, location.state, dispatch, navigate]);

  // ---------------------------------------------------------------------------
  // Effect: Auth timeout for loading state
  // ---------------------------------------------------------------------------
  useEffect(() => {
    // Only start timeout when actively loading
    if (authState === "loading") {
      // Clear any existing timeout
      if (authTimeoutRef.current) {
        clearTimeout(authTimeoutRef.current);
      }
      
      authTimeoutRef.current = setTimeout(() => {
        // Only trigger if still in loading state - use ref to avoid stale closure
        if (authStateRef.current === "loading" && !isLoggingOutRef.current) {
          Sentry.captureMessage("Auth timeout - forcing logout", {
            level: "error",
            tags: { auth_flow: "timeout" },
            extra: {
              pathname: location.pathname,
              hasAuthSession: hasValidAuthSession(),
              hasUserId: !!getSession("userId"),
              hasRole: !!getSession("role"),
            },
          });

          // Hard redirect - most reliable
          forceRedirectToLogin();
        }
      }, AUTH_TIMEOUT_MS);
      
      // Show refresh button after delay
      refreshTimerRef.current = setTimeout(() => {
        setShowRefresh(true);
      }, SHOW_REFRESH_AFTER_MS);

      return () => {
        if (authTimeoutRef.current) {
          clearTimeout(authTimeoutRef.current);
          authTimeoutRef.current = null;
        }
        if (refreshTimerRef.current) {
          clearTimeout(refreshTimerRef.current);
          refreshTimerRef.current = null;
        }
      };
    } else {
      // Auth completed or not started - clear timeout and hide refresh
      if (authTimeoutRef.current) {
        clearTimeout(authTimeoutRef.current);
        authTimeoutRef.current = null;
      }
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      setShowRefresh(false);
    }
  }, [authState, location.pathname]);

  // ---------------------------------------------------------------------------
  // Effect: Cleanup on unmount
  // ---------------------------------------------------------------------------
  useEffect(() => {
    return () => {
      clearAllTimeouts();
    };
  }, [clearAllTimeouts]);

  // ---------------------------------------------------------------------------
  // Effect: Track fresh login for topic subscription
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const hasBeLoginSuccess = !beLoginState.loading && !beLoginState.error && 
      beLoginState.data && Object.keys(beLoginState.data).length > 0;
    const hasGoogleLoginSuccess = !googleLoginState.loading && !googleLoginState.error && 
      googleLoginState.data && Object.keys(googleLoginState.data).length > 0;

    if (hasBeLoginSuccess || hasGoogleLoginSuccess) {
      // Fresh login completed - subscribe to topics after auth completes
      if (authState === "authenticated" && userRole) {
        subscribeToTopicsIfNeeded();
      }
    }
  }, [
    beLoginState.loading, beLoginState.error, beLoginState.data,
    googleLoginState.loading, googleLoginState.error, googleLoginState.data,
    authState, userRole,
  ]);

  // ---------------------------------------------------------------------------
  // Helper: Subscribe to FCM topics
  // ---------------------------------------------------------------------------
  const subscribeToTopicsIfNeeded = useCallback(async () => {
    if (topicSubscribedRef.current) return;
    if (!userRole) return;
    if (!isShopAdmin(userRole) && !isShopSupervisor(userRole)) return;

    topicSubscribedRef.current = true;

    try {
      const token = await getFirebaseMessagingToken();
      if (token) {
        await subscribeUserToTopic(token);
      }
    } catch (error) {
      Sentry.captureException(error, {
        level: "warning",
        tags: { feature: "fcm_subscription" },
      });
      topicSubscribedRef.current = false;
    }
  }, [userRole]);

  // ---------------------------------------------------------------------------
  // Effect: Fetch shops for shop users
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const fetchShops = async () => {
      if (shopsFetchedRef.current) return;
      if (authState !== "authenticated") return;
      if (!userRole || !isShopUser(userRole)) return;

      shopsFetchedRef.current = true;

      try {
        const response = await shopService.fetchAllShopsForLogin();
        const shops = response?.data?.result?.data?.shops || [];
        dispatch(setAllShops(shops));

        // If shopId hasn't been set (e.g. fresh session, or previously persisted
        // shopId was cleared), pick the first valid shop so <ShopDataGate> has
        // a shopId to begin the config/details fetches for.
        const currentShopId = store.getState().allOrderManagement?.shopId;
        if (!currentShopId) {
          const firstShop = shops.find(
            (shop: any) => shop?.firestore?.id && shop?.firestore?.shop_name,
          );
          if (firstShop?.firestore?.id) {
            dispatch(updateShopId(firstShop.firestore.id));
          }
        }
      } catch (error) {
        Sentry.captureException(error, {
          level: "warning",
          tags: { feature: "fetch_shops" },
        });
        shopsFetchedRef.current = false;
      }
    };

    fetchShops();
  }, [authState, userRole, dispatch]);

  // ---------------------------------------------------------------------------
  // Handler: Manual refresh
  // ---------------------------------------------------------------------------
  const handleRefresh = useCallback(() => {
    window.location.reload();
  }, []);

  // ---------------------------------------------------------------------------
  // Spinner timeout: force logout if spinner shows for more than 30 seconds
  // ---------------------------------------------------------------------------
  const spinnerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Determine if we should show a full-screen spinner
  const isOnLoginPage = location.pathname === "/login";
  // On login page: show spinner if there's an existing session being restored
  // (not a fresh login attempt where the user is actively interacting with Login.tsx)
  // A fresh login flow is identified by: beLogin/googleLogin loading OR having success data
  // (meaning the saga just completed and firebaseLogin hasn't finished yet)
  const hasActiveLoginFlow = beLoginState.loading || googleLoginState.loading ||
    firebaseLoginState.loading ||
    (!beLoginState.loading && !beLoginState.error && beLoginState.data && Object.keys(beLoginState.data).length > 0) ||
    (!googleLoginState.loading && !googleLoginState.error && googleLoginState.data && Object.keys(googleLoginState.data).length > 0);
  const isRestoringSessionOnLogin = isOnLoginPage && 
    (authState === "initializing" || authState === "loading") &&
    !hasActiveLoginFlow &&
    (hasValidAuthSession() || !!getSession("customToken") || !!getSession("userId"));
  // Authenticated but still on /login - navigation hasn't completed yet
  const isNavigatingFromLogin = isOnLoginPage && authState === "authenticated";
  // Show spinner for any of these transitional states
  const showFullScreenSpinner = isRestoringSessionOnLogin || isNavigatingFromLogin;

  useEffect(() => {
    if (showFullScreenSpinner) {
      // Start 30-second timeout to force logout
      spinnerTimeoutRef.current = setTimeout(() => {
        Sentry.captureMessage("Spinner timeout - forcing logout after 30s", {
          level: "error",
          tags: { auth_flow: "spinner_timeout" },
          extra: {
            pathname: location.pathname,
            authState,
            hasAuthSession: hasValidAuthSession(),
            hasUserId: !!getSession("userId"),
            hasRole: !!getSession("role"),
          },
        });
        forceRedirectToLogin();
      }, SPINNER_LOGOUT_TIMEOUT_MS);

      return () => {
        if (spinnerTimeoutRef.current) {
          clearTimeout(spinnerTimeoutRef.current);
          spinnerTimeoutRef.current = null;
        }
      };
    } else {
      // Not showing spinner - clear timeout
      if (spinnerTimeoutRef.current) {
        clearTimeout(spinnerTimeoutRef.current);
        spinnerTimeoutRef.current = null;
      }
    }
  }, [showFullScreenSpinner, authState, location.pathname]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  // Full-screen spinner for session restoration on login page or post-auth navigation
  if (showFullScreenSpinner) {
    return <FullScreenSpinner />;
  }

  // On login page during loading/initializing with active login (user clicked login/google)
  // Keep Login.tsx mounted so it can dispatch firebaseLogin after googleLogin/beLogin completes
  if (authState === "loading") {
    if (isOnLoginPage) {
      return <OpenRoutes />;
    }

    return (
      <FullScreenSpinner
        showRetry={showRefresh}
        onRetry={handleRefresh}
      />
    );
  }

  // During initialization
  if (authState === "initializing") {
    // If on login page with no existing session, show login form
    if (isOnLoginPage) {
      return <OpenRoutes />;
    }
    // Otherwise show a brief loader while Firebase checks auth state
    return <FullScreenSpinner message="" />;
  }

  if (authState === "authenticated") {
    return (
      <OrderSSEProvider>
        <AuthRoutes />
      </OrderSSEProvider>
    );
  }

  // authState === "unauthenticated"
  return <OpenRoutes />;
};

export default AppRouter;
